import { defineSseEvent } from '@terra/shared-sse-contracts';
import { z } from 'zod';

export const SmokeTestSseEventTypeEnum = {
  SMOKE_TEST_STARTED: 'SMOKE_TEST_STARTED',
  SMOKE_TEST_TOOL_STARTED: 'SMOKE_TEST_TOOL_STARTED',
  SMOKE_TEST_TOOL_COMPLETED: 'SMOKE_TEST_TOOL_COMPLETED',
  SMOKE_TEST_COMPLETED: 'SMOKE_TEST_COMPLETED',
  SMOKE_TEST_FAILED: 'SMOKE_TEST_FAILED',
  SMOKE_TEST_MESSAGE_ADDED: 'SMOKE_TEST_MESSAGE_ADDED',
  SMOKE_TEST_STATUS_CHANGED: 'SMOKE_TEST_STATUS_CHANGED',
} as const;

export const SmokeTestEventPayloadSchema = z.object({
  threadId: z.string(),
  toolId: z.string().optional(),
  status: z.string().optional(),
  timestamp: z.string().optional(),
});

export const SmokeTestStatusChangePayloadSchema = z.object({
  signalId: z.string(),
  applicationId: z.string(),
  status: z.union([
    z.literal('STARTED'),
    z.literal('COMPLETED'),
    z.literal('FAILED'),
    z.literal('STOPPED'),
  ]),
  executionId: z.string().optional(),
  threadId: z.string().optional(),
});

const buildSmokeTestEvent = (
  type: Exclude<
    (typeof SmokeTestSseEventTypeEnum)[keyof typeof SmokeTestSseEventTypeEnum],
    'SMOKE_TEST_STATUS_CHANGED'
  >,
) =>
  defineSseEvent({
    type,
    payload: SmokeTestEventPayloadSchema,
    channelPrefix: 'smoke-test',
    channelKey: ({ threadId }) => threadId,
  });

export const SMOKE_TEST_STARTED_SSE_EVENT = buildSmokeTestEvent(
  SmokeTestSseEventTypeEnum.SMOKE_TEST_STARTED,
);
export const SMOKE_TEST_TOOL_STARTED_SSE_EVENT = buildSmokeTestEvent(
  SmokeTestSseEventTypeEnum.SMOKE_TEST_TOOL_STARTED,
);
export const SMOKE_TEST_TOOL_COMPLETED_SSE_EVENT = buildSmokeTestEvent(
  SmokeTestSseEventTypeEnum.SMOKE_TEST_TOOL_COMPLETED,
);
export const SMOKE_TEST_COMPLETED_SSE_EVENT = buildSmokeTestEvent(
  SmokeTestSseEventTypeEnum.SMOKE_TEST_COMPLETED,
);
export const SMOKE_TEST_FAILED_SSE_EVENT = buildSmokeTestEvent(
  SmokeTestSseEventTypeEnum.SMOKE_TEST_FAILED,
);
export const SMOKE_TEST_MESSAGE_ADDED_SSE_EVENT = buildSmokeTestEvent(
  SmokeTestSseEventTypeEnum.SMOKE_TEST_MESSAGE_ADDED,
);

export const SMOKE_TEST_STATUS_CHANGED_SSE_EVENT = defineSseEvent({
  type: SmokeTestSseEventTypeEnum.SMOKE_TEST_STATUS_CHANGED,
  payload: SmokeTestStatusChangePayloadSchema,
  channelPrefix: 'smoke-test',
  channelKey: ({ applicationId, signalId }) => `${applicationId}:${signalId}`,
});

export type SmokeTestEventPayload = z.infer<typeof SmokeTestEventPayloadSchema>;
export type SmokeTestStatusChangePayload = z.infer<
  typeof SmokeTestStatusChangePayloadSchema
>;
